import { ROOT, type WizardFieldErrors } from "@/base";
import { ApiError } from "@/lib/api";
import type { MissingField } from "@/lib/brokerProducts";
import type {
  AnswerSource,
  CarrierIndication,
  CarrierQuestionSet,
  CarrierStepError,
  IncompleteCarrier,
  QuoteAnswer,
  SubmissionCarrier,
} from "@/lib/submissions/brokerSubmissions";
import {
  roundOfAnswers,
  savedSource,
  savedValues,
  unansweredRequired,
  type AnswerValues,
} from "@/lib/submissions/carrierQuestions";

// The WC wizard's last step (BRK-S393, BRK-S491), as pure rules. Opening it
// reads the carriers PMS has in play (PMS-S815), then sends the application to
// every one at once, with no button: the fan-out, and each carrier's questions
// the application can't answer (Great American's read creates its application
// there). The broker answers them here, Get Quotes saves each carrier's
// answers, and PMS refuses it while a carrier has questions left, which lands
// on that carrier. The step shows only what the broker must answer; the
// indications show with the quotes (Nav, 2026-10-28, superseding 10/22's
// "Broker will see the indications at last step").

/** One carrier on the last step, as PMS answered and the broker answers. */
export interface WcCarrier {
  carrierId: string;
  carrier: string;
  quotingMode: "api" | "manual";
  /** Its fan-out leg: undefined while the fan-out runs, null when none came
   *  back (a manual carrier has none). */
  indication: CarrierIndication | null | undefined;
  /** Its questions as PMS last returned them: undefined while they're read,
   *  null for a manual carrier, which is asked nothing. */
  set: CarrierQuestionSet | null | undefined;
  /** Its questions couldn't be read, shown in its place. */
  problem: CarrierStepError | null;
  /** The answers, as the broker edits them. */
  values: AnswerValues;
  source: AnswerSource;
}

/** The step as it stands. Every read lands through the wizard's `setLoaded`,
 *  so only the broker's answers read as unsaved changes. */
export interface WcCarrierStep {
  /** Null while the carriers in play are read. */
  carriers: WcCarrier[] | null;
  /** The carriers in play couldn't be read: why. */
  problem: string | null;
  /** The fan-out failed as a whole: the application isn't ready (its missing
   *  fields), or PMS refused it. */
  fanOutProblem: { message: string; missing: MissingField[] | null } | null;
}

export const CARRIERS_LOADING = "The carriers are still loading.";
export const ANSWER_THIS = "Answer this question.";
export const CARRIER_ANSWERS_REFUSED =
  "Some answers weren’t saved. Check each carrier below.";

/** The carriers in play, in PMS's order. A mode the portal doesn't know
 *  fires nothing and shows nothing (PMS-S819 adds `manual`). */
export function carriersFrom(list: readonly SubmissionCarrier[]): WcCarrier[] {
  return list.flatMap((c): WcCarrier[] =>
    c.quotingMode === null
      ? []
      : [
          {
            carrierId: c.carrierId,
            carrier: c.carrierName,
            quotingMode: c.quotingMode,
            indication: c.quotingMode === "api" ? undefined : null,
            set: c.quotingMode === "api" ? undefined : null,
            problem: null,
            values: {},
            source: "user",
          },
        ],
  );
}

/** Each API carrier's fan-out leg, or none when the fan-out named it not.
 *  Kept for the quotes screen, which Get Quotes hands them to: the step
 *  itself shows none (BRK-S491). */
export function withIndications(
  carriers: readonly WcCarrier[],
  legs: readonly CarrierIndication[],
): WcCarrier[] {
  return carriers.map((c) =>
    c.quotingMode !== "api"
      ? c
      : {
          ...c,
          indication: legs.find((l) => l.carrierId === c.carrierId) ?? null,
        },
  );
}

/** A carrier's questions as PMS returned them, with their saved answers and
 *  who gave them. */
export function withQuestionSet(
  carriers: readonly WcCarrier[],
  carrierId: string,
  set: CarrierQuestionSet,
): WcCarrier[] {
  return carriers.map((c) =>
    c.carrierId === carrierId
      ? {
          ...c,
          set,
          problem: null,
          values: savedValues(set.questions),
          source: savedSource(set.questions),
        }
      : c,
  );
}

/** A carrier's questions read that failed, or is being read again. */
export function withQuestionsProblem(
  carriers: readonly WcCarrier[],
  carrierId: string,
  problem: CarrierStepError | null,
): WcCarrier[] {
  return carriers.map((c) =>
    c.carrierId === carrierId
      ? { ...c, problem, ...(problem === null ? { set: undefined } : {}) }
      : c,
  );
}

/** What the step shows (BRK-S491; Nav, 2026-10-28): only what the broker
 *  must answer. `cards` are the carriers that ask, one still being sent the
 *  application, one whose questions couldn't be read, and one that declined
 *  on an answer the broker can change, in PMS's order; `ready` are those with
 *  nothing more to ask, named in one line, unless Submit's refusal `flagged`
 *  one. A manual carrier and a decline the broker can't change aren't on the
 *  step: the quotes screen shows them, with every indication. */
export function askingCarriers(
  carriers: readonly WcCarrier[],
  flagged: ReadonlySet<string> = new Set(),
): { cards: WcCarrier[]; ready: WcCarrier[] } {
  const cards: WcCarrier[] = [];
  const ready: WcCarrier[] = [];
  for (const c of carriers) {
    if (c.quotingMode !== "api") continue;
    if (c.set?.declined && c.set.questions.length === 0) continue;
    if (nothingMoreNeeded(c) && !flagged.has(c.carrierId)) ready.push(c);
    else cards.push(c);
  }
  return { cards, ready };
}

/** Carriers' names as a sentence says them: "Pie and Acme". */
export function carrierNames(carriers: readonly { carrier: string }[]): string {
  return new Intl.ListFormat("en-US", {
    style: "long",
    type: "conjunction",
  }).format(carriers.map((c) => c.carrier));
}

/** Whether a carrier needs nothing more: its set complete, nothing showing
 *  unanswered, and nothing changed since it was saved. */
export function nothingMoreNeeded(c: WcCarrier): boolean {
  const set = c.set;
  if (!set || set.declined || !set.complete) return false;
  return (
    unansweredRequired(set.questions, c.values).length === 0 &&
    roundOfAnswers(set.questions, c.values, c.source).length === 0
  );
}

/** Whether this carrier's questions are on the step. */
function asked(c: WcCarrier): c is WcCarrier & { set: CarrierQuestionSet } {
  return c.quotingMode === "api" && !!c.set;
}

/** Whether the broker answers this carrier here: one that declined is
 *  answered only to change what it declined on. */
function answering(c: WcCarrier): c is WcCarrier & { set: CarrierQuestionSet } {
  return asked(c) && !c.set.declined;
}

/** Where a carrier's line, and one of its questions, are keyed among the
 *  wizard's errors. A carrier id is a uuid, so the first dot after it
 *  starts the question id. */
const carrierKey = (carrierId: string) => `carriers.${carrierId}`;
const questionKey = (carrierId: string, questionId: string) =>
  `carriers.${carrierId}.${questionId}`;
const REREAD = "reread.";

/**
 * Submit's checks, before anything is saved: the carriers read, and every
 * showing required question answered, keyed on its carrier. A carrier whose
 * read failed, or that declined, is PMS's to judge at Submit.
 */
export function validateCarrierStep(
  step: WcCarrierStep | null,
): WizardFieldErrors {
  const carriers = step?.carriers;
  if (
    !carriers ||
    carriers.some(
      (c) => c.quotingMode === "api" && c.set === undefined && !c.problem,
    )
  )
    return { [ROOT]: CARRIERS_LOADING };
  const errors: Record<string, string> = {};
  for (const c of carriers.filter(answering)) {
    for (const q of unansweredRequired(c.set.questions, c.values)) {
      errors[questionKey(c.carrierId, q.id)] = ANSWER_THIS;
    }
  }
  return errors;
}

/** Each carrier's answers to save, one round per carrier with any: a
 *  carrier that declined on an answer included, so a corrected one lets it
 *  reassess (BRK-S370). */
export function carrierRounds(
  step: WcCarrierStep | null,
): { carrierId: string; answers: QuoteAnswer[] }[] {
  return (step?.carriers ?? []).filter(asked).flatMap((c) => {
    const answers = roundOfAnswers(c.set.questions, c.values, c.source);
    return answers.length > 0 ? [{ carrierId: c.carrierId, answers }] : [];
  });
}

/** One carrier's refused answers, keyed where its section shows them. */
export function carrierFieldErrors(
  carrierId: string,
  byQuestion: Readonly<Record<string, string>>,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(byQuestion).map(([id, message]) => [
      questionKey(carrierId, id),
      message,
    ]),
  );
}

/** A carrier's own line among the errors, such as why its round failed. */
export function carrierLineError(
  carrierId: string,
  line: string,
): Record<string, string> {
  return { [carrierKey(carrierId)]: line };
}

/** Submit's carriers with questions left (PMS-S815), as the wizard's errors:
 *  each carrier's line and its missing questions, and a re-read for one whose
 *  questions changed with the application. */
export function incompleteAsFieldErrors(
  incomplete: readonly IncompleteCarrier[],
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const { carrierId, carrierName, missing } of incomplete) {
    if (missing.length === 0) {
      errors[carrierKey(carrierId)] =
        `${carrierName}’s questions changed with the application. Check them again.`;
      errors[`${REREAD}${carrierId}`] = carrierName;
      continue;
    }
    errors[carrierKey(carrierId)] =
      `Answer ${carrierName}’s remaining questions.`;
    for (const id of missing) errors[questionKey(carrierId, id)] = ANSWER_THIS;
  }
  return errors;
}

/** The carriers whose questions Submit said to read again. */
export function rereadCarriers(errors: WizardFieldErrors): string[] {
  return Object.keys(errors)
    .filter((k) => k.startsWith(REREAD))
    .map((k) => k.slice(REREAD.length));
}

/** One carrier's errors: its line, and its questions'. */
export function carrierErrors(
  errors: WizardFieldErrors,
  carrierId: string,
): { line: string | null; questions: Record<string, string> } {
  const prefix = `${carrierKey(carrierId)}.`;
  const questions: Record<string, string> = {};
  for (const [key, message] of Object.entries(errors)) {
    if (key.startsWith(prefix)) questions[key.slice(prefix.length)] = message;
  }
  return { line: errors[carrierKey(carrierId)] ?? null, questions };
}

/** What Submit still needs from the carriers, carrier by carrier: each one's
 *  line, and its questions left by their prompts. */
export function stillNeeded(
  step: WcCarrierStep | null,
  errors: WizardFieldErrors,
): {
  carrierId: string;
  carrier: string;
  line: string;
  questions: { questionId: string; prompt: string }[];
}[] {
  return (step?.carriers ?? []).flatMap((c) => {
    const { line, questions } = carrierErrors(errors, c.carrierId);
    if (!line) return [];
    const asked = c.set?.questions ?? [];
    return [
      {
        carrierId: c.carrierId,
        carrier: c.carrier,
        line,
        questions: Object.keys(questions).flatMap((id) => {
          const q = asked.find((x) => x.id === id);
          return q ? [{ questionId: id, prompt: q.prompt }] : [];
        }),
      },
    ];
  });
}

/**
 * After Get Quotes saved the carriers' answers: the refusal that stops it
 * before PMS is asked for quotes, when a round opened more questions (BRK-S497;
 * Great American's business type opens its questionnaire, PMS-S905). Null
 * when nothing new is left to answer. Each new question is marked, and the
 * summary names the carriers that ask.
 */
export function moreQuestionsRefusal(
  step: WcCarrierStep | null,
): ApiError | null {
  const left = validateCarrierStep(step);
  if (Object.keys(left).length === 0 || left[ROOT]) return null;
  const asking = (step?.carriers ?? []).filter(
    (c) => Object.keys(carrierErrors(left, c.carrierId).questions).length > 0,
  );
  if (asking.length === 0) return null;
  const message = `${carrierNames(asking)} ${asking.length === 1 ? "has" : "have"} more questions now. Answer them, then press Get Quotes.`;
  return new ApiError(400, message, {
    error: message,
    details: { fieldErrors: { [ROOT]: message, ...left } },
  });
}
